import {css, StyleSheet} from 'aphrodite';
import React from 'react';
import _ from 'underscore';

import {assert} from 'Asserts';
import BenchlingConfig from 'BenchlingConfig';
import {pluralize} from 'BenchlingUtil';
import ModelTypeConstants from 'constants/ModelTypeConstants';
import {LookupConfigSpec, SchemaById} from 'lookup-config/LookupConfigModalConstants';
import {MappableProcessType, PROCESS_MANAGEMENT_ITEM_TYPE_TO_READABLE} from 'processes/ProcessConstants';
import {
  FIELD_TYPES,
  FieldType,
  getRequiredLinkFolderItemType,
  getRequiredLinkSchemaId,
  getRequiredLinkStorableSchema,
  SchemaField,
} from 'schemas/fields/SchemaFieldConstants';
import {FONT_PROXIMA_13} from 'styles/FontConstants';
import getTestProps from 'test/lib/cypress/getTestProps';
import InlineBanner, {InlineBannerType} from 'ui-library/banners/InlineBanner';
import Tooltip from 'ui-library/bs/Tooltip';
import * as Colors from 'ui-library/Colors';
import BenchlingIcon, {BenchlingIconName} from 'ui-library/icons/BenchlingIcon';
import {FolderItemIcon} from 'ui-library/icons/IconConstants';
import CheckboxInput from 'ui-library/inputs/CheckboxInput';
import {FieldRequiredIndicator} from 'ui-library/inputs/FieldLabel';
import {LabelStyle} from 'ui-library/inputs/InputConstants';
import {Cell, Column, Table} from 'ui-library/simple-table/SimpleTable';
import APIUtil from 'util/APIUtil';
import {typeToName} from 'util/NameUtil';

import {ProcessTaskSchemaGraphQL} from '../../../../graphql-fragments/processes/schemas/__generated/ProcessTaskSchemaGraphQL';
import FieldMapperSourceDropdown from './FieldMapperSourceDropdown';
import {FieldToFieldMapperData, getSchemaFromSchemaInfo, SchemaInfo} from './FieldMappingConstants';
import MapperSourcesTable from './MapperSourcesTable';
import SchemaFieldLookup from './SchemaFieldLookup';

function getIconForSchemaField(schemaField: SchemaField): BenchlingIconName {
  switch (schemaField.fieldType) {
    case FieldType.ASSAY_REQUEST_LINK:
      return ModelTypeConstants.REQUEST;
    case FieldType.ASSAY_RESULT_LINK:
      return ModelTypeConstants.RESULT_SCHEMA;
    case FieldType.ASSAY_RUN_LINK:
      return ModelTypeConstants.ASSAY_RUN;
    case FieldType.BATCH_LINK:
      return ModelTypeConstants.BATCH;
    case FieldType.BLOB_LINK:
      return 'external_file';
    case FieldType.BOOLEAN:
      return 'boolean';
    case FieldType.DATE:
      return 'calendar';
    case FieldType.DATETIME:
      return 'eln-timestamp';
    case FieldType.ENTRY_LINK:
      return FolderItemIcon.Entry;
    case FieldType.BIOENTITY_LINK:
      return ModelTypeConstants.BIOENTITY_MIXIN;
    case FieldType.FLOAT:
      return 'number';
    case FieldType.INTEGER:
      return 'number';
    case FieldType.JSON:
      return 'brackets';
    case FieldType.LONG_TEXT:
      return 'text';
    case FieldType.PART_LINK:
      return ModelTypeConstants.SEQUENCE_PRIMER;
    case FieldType.SAMPLE_LINK:
      return ModelTypeConstants.SAMPLE;
    case FieldType.SELECTOR:
      return 'schema_field_selector';
    case FieldType.STORABLE_LINK: {
      const storableSchema = getRequiredLinkStorableSchema(schemaField);
      if (storableSchema == null) {
        return ModelTypeConstants.CONTAINER;
      }
      if (APIUtil.isContainerSchema(storableSchema.id)) {
        return ModelTypeConstants.CONTAINER;
      } else if (APIUtil.isPlateSchema(storableSchema.id)) {
        return ModelTypeConstants.PLATE;
      } else if (APIUtil.isBoxSchema(storableSchema.id)) {
        return ModelTypeConstants.BOX;
      } else if (APIUtil.isLocationSchema(storableSchema.id)) {
        return ModelTypeConstants.LOCATION;
      }
      return ModelTypeConstants.CONTAINER;
    }
    case FieldType.TEXT:
      return 'text';
    case FieldType.TRANSCRIPTION_LINK:
      return FolderItemIcon.RNASequence;
    case FieldType.TRANSLATION_LINK:
      return ModelTypeConstants.SEQUENCE_PRIMER;
    default:
      assert(false, `Unexpected schema field type ${schemaField.fieldType}`);
      return 'unknown';
  }
}

function FieldInfoIcon({
  schemaField,
  allSchemasById,
}: {
  schemaField: SchemaField;
  allSchemasById: SchemaById;
}): JSX.Element {
  const icon = getIconForSchemaField(schemaField);
  const requiredLinkSchemaId = getRequiredLinkSchemaId(schemaField);
  const requiredLinkSchema = requiredLinkSchemaId != null ? allSchemasById[requiredLinkSchemaId] : null;
  const requiredLinkFolderItemType = getRequiredLinkFolderItemType(schemaField);

  let tooltipTitle;
  const fieldLabel = Object.values(FIELD_TYPES).find(({type}) => type === schemaField.fieldType)!.label;
  if (requiredLinkSchema != null) {
    tooltipTitle = `${fieldLabel} (${requiredLinkSchema.name})`;
  } else if (requiredLinkFolderItemType != null) {
    tooltipTitle = `${fieldLabel} (Any ${typeToName(requiredLinkFolderItemType)})`;
  } else {
    tooltipTitle = fieldLabel;
  }

  return (
    <Tooltip title={tooltipTitle}>
      <BenchlingIcon icon={icon} className={css(styles.fieldIcon)} />
    </Tooltip>
  );
}

interface SchemaMapperConfigModalBodyProps {
  flowchartTaskSchema: ProcessTaskSchemaGraphQL;
  onChangeFromItemType: (itemType: MappableProcessType, idx: number) => void;
  onChangeFieldMapperSource: (fieldId: string, sourceTaskSchemaId: string) => void;
  onChangeResultMustBeNonNullToMap: (fieldId: string, resultMustBeNonNullToMap: boolean) => void;
  disabled?: boolean;
  fromSchemaInfos: Array<SchemaInfo>;
  toSchemaInfo: SchemaInfo;
  fieldToFieldMapperData: FieldToFieldMapperData;
  onSubmit: (fieldId: string, lookupConfig: LookupConfigSpec) => void;
  removeLookupConfigForField: (fieldId: string) => void;
  allSchemasById: SchemaById;
  errorMessage: string | null;
}

export default function SchemaMapperConfigModalBody(props: SchemaMapperConfigModalBodyProps): JSX.Element {
  const toSchema = getSchemaFromSchemaInfo(props.toSchemaInfo);
  const mappableFields = toSchema.fields.filter((field) => !field.isComputed && !field.isSnapshot);

  return (
    <div>
      <MapperSourcesTable
        disabled={props.disabled}
        fromSchemaInfos={props.fromSchemaInfos}
        onChangeFromItemType={props.onChangeFromItemType}
      />
      <div className={css(styles.modalText)}>
        {props.disabled ? 'Mapping' : 'Map to'} fields in{' '}
        <strong>{props.toSchemaInfo.taskSchema.name}</strong> from fields in{' '}
        <strong>{props.fromSchemaInfos.map(({taskSchema}) => taskSchema.name).join(', ')}</strong>
      </div>
      <div>
        {mappableFields.length > 0 ? (
          <Table numRows={mappableFields.length}>
            <Column
              header={props.disabled ? 'Mapping to' : 'Map to'}
              renderCell={(rowIndex) => (
                <Cell>
                  <FieldInfoIcon
                    schemaField={mappableFields[rowIndex]}
                    allSchemasById={props.allSchemasById}
                  />
                  {mappableFields[rowIndex].name}
                  {mappableFields[rowIndex].isRequired && <FieldRequiredIndicator />}
                </Cell>
              )}
            />
            {props.fromSchemaInfos.length > 1 && (
              <Column
                header="Source schema"
                renderCell={(rowIndex) => {
                  let selectedTaskSchema;
                  const {fromSchemaId} = props.fieldToFieldMapperData[mappableFields[rowIndex].apiId];
                  if (fromSchemaId == null) {
                    selectedTaskSchema = null;
                  } else {
                    selectedTaskSchema = props.fromSchemaInfos.find(
                      ({taskSchema}) =>
                        taskSchema.id === fromSchemaId || taskSchema.outputSchema?.id === fromSchemaId
                    )!.taskSchema;
                  }
                  return (
                    <Cell>
                      <FieldMapperSourceDropdown
                        disabled={props.disabled}
                        possibleTaskSchemas={props.fromSchemaInfos.map(({taskSchema}) => taskSchema)}
                        onSelect={(id) => {
                          props.onChangeFieldMapperSource(mappableFields[rowIndex].apiId, id);
                        }}
                        selectedTaskSchema={selectedTaskSchema}
                        dataTestAttrs={{
                          component: 'SchemaMapperConfigModalBody',
                          element: 'fieldMapperSourceDropdown',
                          key: mappableFields[rowIndex].apiId,
                        }}
                      />
                    </Cell>
                  );
                }}
              />
            )}
            <Column
              header={props.disabled ? 'Mapping from' : 'Map from'}
              renderCell={(rowIndex) => {
                const {lookupConfig, fromSchemaId} =
                  props.fieldToFieldMapperData[mappableFields[rowIndex].apiId];
                const fromSchema = fromSchemaId != null ? (props.allSchemasById[fromSchemaId] ?? null) : null;
                return (
                  <Cell>
                    <SchemaFieldLookup
                      flowchartTaskSchema={props.flowchartTaskSchema}
                      disabled={props.disabled}
                      lookupConfig={lookupConfig}
                      fromSchema={fromSchema}
                      toSchemaField={mappableFields[rowIndex]}
                      onSubmit={(lookupConfig: LookupConfigSpec) => {
                        props.onSubmit(mappableFields[rowIndex].apiId, lookupConfig);
                      }}
                      onRemove={() => {
                        props.removeLookupConfigForField(mappableFields[rowIndex].apiId);
                      }}
                      {...getTestProps({
                        component: 'SchemaMapperConfigModalBody',
                        element: 'schemaFieldLookup',
                        key: mappableFields[rowIndex].apiId,
                      })}
                    />
                  </Cell>
                );
              }}
            />
            {BenchlingConfig.WORKFLOWS_ENABLE_CONDITIONAL_MAPPING && (
              <Column
                header={
                  <Cell>
                    <span>
                      <span>Require field to map</span>
                      <Tooltip
                        title={`${props.disabled ? 'Prevent' : 'Check the box to prevent'} ${pluralize(
                          2,
                          PROCESS_MANAGEMENT_ITEM_TYPE_TO_READABLE[props.toSchemaInfo.itemType]
                        )} with empty or null values for the specified field from being created.`}
                      >
                        <BenchlingIcon icon="info-circle" className={css(styles.helpIcon)} />
                      </Tooltip>
                    </span>
                  </Cell>
                }
                renderCell={(rowIndex) => {
                  const fieldId = mappableFields[rowIndex].apiId;
                  const {resultMustBeNonNullToMap} = props.fieldToFieldMapperData[fieldId];
                  const inputId = _.uniqueId('resultMustBeNonNullToMap');
                  return (
                    <Cell>
                      <CheckboxInput
                        id={inputId}
                        disabled={props.disabled}
                        checked={resultMustBeNonNullToMap}
                        label="Require field to map"
                        labelStyle={LabelStyle.HIDDEN}
                        onChange={(e) => {
                          props.onChangeResultMustBeNonNullToMap(fieldId, e.target.checked);
                        }}
                      />
                    </Cell>
                  );
                }}
              />
            )}
          </Table>
        ) : (
          <div className={css(styles.modalText)}>
            <strong>{props.toSchemaInfo.taskSchema.name}</strong> has no mappable schema fields.
          </div>
        )}
        {props.errorMessage && (
          <InlineBanner bannerType={InlineBannerType.WARNING} text={props.errorMessage} />
        )}
      </div>
    </div>
  );
}

const styles = StyleSheet.create({
  modalText: {
    ...FONT_PROXIMA_13,
    paddingBottom: 16,
    paddingTop: 22,
    paddingLeft: 6,
  },
  fieldIcon: {
    color: Colors.GRAY_DARK,
    fontSize: 12,
    marginRight: 8,
  },
  helpIcon: {
    marginLeft: 8,
  },
});
